import { useEffect, useMemo, useRef } from 'react'

import { queueTrackingEvent } from '@library/tracking-integration'

import { useCollections } from 'hooks/menu-collections'
import type {
  LoadMerchBannerEvent,
  MerchHeaderType,
} from 'routes/Menu/MenuExperience/MenuPage/types'
import type {
  CampaignBannerHeaderContentBlock,
  CategoryBrandingContentBlock,
  CompactCampaignBannerHeaderContentBlock,
  MerchandisingHeaderContentBlock,
} from 'routes/Menu/MenuRecipesPage/CollectionHeader'

type CampaignMerchandisingHeader =
  | CampaignBannerHeaderContentBlock
  | CompactCampaignBannerHeaderContentBlock
type TrackedMerchandisingHeader = CategoryBrandingContentBlock | CampaignMerchandisingHeader

type UseLoadMerchBannerTrackingProps = {
  collectionId: LoadMerchBannerEvent['properties']['current_collection_id']
  collectionSlug?: LoadMerchBannerEvent['properties']['current_collection_slug']
  isMerchandisingBannerVisible: boolean
  isMerchandisingHeadersLoading: boolean
  menuUuid: string
  merchandisingHeader?: MerchandisingHeaderContentBlock | null
}

const isCampaignMerchandisingHeader = (
  merchandisingHeader?: MerchandisingHeaderContentBlock | null,
): merchandisingHeader is CampaignMerchandisingHeader =>
  merchandisingHeader?.type === 'campaign-banner' ||
  merchandisingHeader?.type === 'compact-campaign-banner'

const isTrackedMerchandisingHeader = (
  merchandisingHeader?: MerchandisingHeaderContentBlock | null,
): merchandisingHeader is TrackedMerchandisingHeader =>
  merchandisingHeader?.type === 'category-branding' ||
  isCampaignMerchandisingHeader(merchandisingHeader)

const getCampaignMerchandisingHeaderTargetCollectionId = (
  merchandisingHeader: CampaignMerchandisingHeader,
): string | null => {
  if (merchandisingHeader.type === 'compact-campaign-banner') {
    return merchandisingHeader.targetCategoryId
  }

  return (
    merchandisingHeader.items.find(
      (item): item is Extract<CampaignBannerHeaderContentBlock['items'][number], { type: 'cta' }> =>
        item.type === 'cta',
    )?.targetCategoryId ?? null
  )
}

const getCampaignMerchandisingHeaderRecipeUuids = (
  merchandisingHeader: CampaignMerchandisingHeader,
): string[] => {
  if (merchandisingHeader.type === 'compact-campaign-banner') {
    return merchandisingHeader.items.map((item) => item.id)
  }

  return merchandisingHeader.items.flatMap((item) => (item.type === 'recipe' ? [item.id] : []))
}

export const getMerchandisingHeaderType = (
  merchandisingHeader?: MerchandisingHeaderContentBlock | null,
): MerchHeaderType | null => {
  if (!isTrackedMerchandisingHeader(merchandisingHeader)) {
    return null
  }

  return merchandisingHeader.type as MerchHeaderType
}

export const useLoadMerchBannerTracking = ({
  collectionId,
  collectionSlug,
  isMerchandisingBannerVisible,
  isMerchandisingHeadersLoading,
  menuUuid,
  merchandisingHeader,
}: UseLoadMerchBannerTrackingProps) => {
  const trackedMerchBannerKeysRef = useRef<Set<string>>(new Set())
  const collections = useCollections()
  const collectionSlugById = useMemo(
    () =>
      Object.fromEntries(collections.map(({ id, slug }) => [id, slug])) as Record<string, string>,
    [collections],
  )

  useEffect(() => {
    if (
      isMerchandisingHeadersLoading ||
      !isMerchandisingBannerVisible ||
      !collectionSlug ||
      !menuUuid ||
      !isCampaignMerchandisingHeader(merchandisingHeader)
    ) {
      return
    }

    const targetCollectionId = getCampaignMerchandisingHeaderTargetCollectionId(merchandisingHeader)
    const targetCollectionSlug = targetCollectionId
      ? collectionSlugById[targetCollectionId] ??
        (targetCollectionId === collectionId ? collectionSlug : undefined)
      : undefined

    if (!targetCollectionId || !targetCollectionSlug) {
      return
    }

    const recipeUuidsShown = getCampaignMerchandisingHeaderRecipeUuids(merchandisingHeader)
    const merchBannerKey = [
      menuUuid,
      collectionId,
      merchandisingHeader.type,
      targetCollectionId,
    ].join(':')

    if (trackedMerchBannerKeysRef.current.has(merchBannerKey)) {
      return
    }

    queueTrackingEvent<LoadMerchBannerEvent>({
      action: 'load_merch_banner',
      properties: {
        action: 'load',
        current_collection_id: collectionId,
        current_collection_slug: collectionSlug,
        initial_view_flag: true,
        menu_uuid: menuUuid,
        merch_header_type: merchandisingHeader.type,
        target_collection_id: targetCollectionId,
        target_collection_slug: targetCollectionSlug,
        recipe_uuids_shown: recipeUuidsShown,
      },
    })

    trackedMerchBannerKeysRef.current.add(merchBannerKey)
  }, [
    collectionId,
    collectionSlug,
    collectionSlugById,
    isMerchandisingBannerVisible,
    isMerchandisingHeadersLoading,
    menuUuid,
    merchandisingHeader,
  ])
}
